import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { readApiError } from '@/state/billing/api-error';
import { ONBOARDING_KEY } from './useOnboarding';

type CreatedWebsite = { id: number; url: string };

/**
 * The actions behind the onboarding steps. State lives here, not in the step
 * components.
 *
 * The website step ends on the snippet screen (`createdWebsite`) and only
 * `finish` leaves onboarding: see useOnboarding for why nothing refetches before
 * then. An agency has no website step: its onboarding is the fee, and its clients
 * (each with a plan and websites of their own) are added from the dashboard.
 */
type UseOnboardingFlowParams = {
  /** An agency's first website goes on an account of its own, included in its fee, not on the agency company. */
  isAgency?: boolean;
};

export const useOnboardingFlow = ({ isAgency = false }: UseOnboardingFlowParams = {}) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isSaving, setIsSaving] = useState(false);
  const [createdWebsite, setCreatedWebsite] = useState<CreatedWebsite | null>(null);
  // An agency's own account, once made, so a website that failed to save does not make a second one.
  const [ownAccountId, setOwnAccountId] = useState<number | null>(null);

  const failure = (title: string) => (error: unknown) => {
    // useClient rejects with `{ status, message }`, not an Error, so the server's own words
    // (a 400, 402 or 409) are read from there; our local checks do throw Errors.
    const message = error instanceof Error ? error.message : readApiError(error).message;
    toast.error(title, { description: message ?? 'Please try again in a moment.' });
  };

  const refreshOnboarding = () => queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });

  /**
   * Saves the address already read and normalised by the page (parseWebsiteAddress), as the
   * account's first website, or, once there is one, changes that website's address in
   * place: changing the domain in the form must not leave a second website behind.
   * Resolves whether it saved, so the page only moves on when it did.
   */
  const saveWebsite = async (href: string): Promise<boolean> => {
    if (!user) return false;
    setIsSaving(true);
    try {
      if (createdWebsite) {
        const website = await apiClient.put<CreatedWebsite>(`/websites/${createdWebsite.id}`, { url: href });
        setCreatedWebsite({ id: createdWebsite.id, url: website.url });
      } else {
        // An agency's own business is an account of its own, named after the agency and included in its
        // fee (the server makes it, and refuses a second); anyone else's website goes on their company.
        let targetCompanyId = user.company.id;
        if (isAgency) {
          const accountId = ownAccountId ?? (await apiClient.post<{ id: number }>('/companies', { included: true })).id;
          setOwnAccountId(accountId);
          targetCompanyId = accountId;
        }
        const website = await apiClient.post<CreatedWebsite>('/websites', { companyId: targetCompanyId, url: href });
        setCreatedWebsite({ id: website.id, url: website.url });
      }
      return true;
    } catch (error) {
      failure('Could not save the website')(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /** Leaves onboarding for the dashboard. */
  const finish = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['WEBSITES'] }),
      queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] }),
      queryClient.invalidateQueries({ queryKey: ['CURRENT_USER'] }),
    ]);
    navigate('/', { replace: true });
    await refreshOnboarding();
  };

  return { isSaving, createdWebsite, saveWebsite, finish };
};

export type OnboardingFlow = ReturnType<typeof useOnboardingFlow>;
